import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { apiClient, authStore, navigerUd } from '../../../core'
import { t } from '../../../core/i18n'
import type { RetreatBookingStrings } from '../../../core/i18n'
import type {
  CreateBookingBody,
  CreateBookingResponse,
  CustomQuestion,
  PaymentMode,
  RetreatData,
  RetreatPackageTravel,
  RetreatTier,
  RoommatePreference,
} from '../types'
import {
  computePriceSummary,
  formatRetreatPrice,
} from './pricing'
import {
  invalidParticipantFields,
  useBookingState,
  type ParticipantDraft,
  type ParticipantField,
  type StepNum,
} from './booking-state'
import { CheckoutLegal, checkoutConsent, initialCheckoutLegalState, type CheckoutLegalState } from '../../../components/CheckoutLegal'
import type { InitData } from '../../../core/widget-base'
import { fmtFrist, studietsTidszone } from '../../../core/tidszone'

/** Replace {placeholders} in a template string. */
function interp(tpl: string, vars: Record<string, string | number>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`))
}

interface Props {
  retreat: RetreatData
  tier: RetreatTier
  /** Tenant UI locale (initData.locale). */
  locale: string
  /** Sælger og links til ordreoversigten (jura-hs p44). */
  initData: InitData
  onClose: () => void
}

/**
 * RetreatBookingModal — the full multi-step retreat booking flow, running inline
 * in a modal on the studio's own site. Ported from the tenant CMS modal so the
 * embedded experience is identical: the visitor completes who/participants/
 * questions/payment-choice here and only leaves the site at the actual payment
 * step (Stripe redirect returned by the widget book endpoint). Guests allowed;
 * a logged-in widget visitor is linked to the booking automatically.
 */
export function RetreatBookingModal({ retreat, tier, locale, initData, onClose }: Props) {
  const S = t(locale).retreatBooking
  // Sælger, påkrævet flueben og betalingspligtig knap på sidste trin (jura-hs p44). Et
  // retreat på bestemte datoer har ingen fortrydelsesret.
  const sLegal = t(locale).checkoutLegal
  const [legal, setLegal] = useState<CheckoutLegalState>(initialCheckoutLegalState)
  const dateLocale = locale

  const prefill = useMemo(() => {
    const u = authStore.getUser()
    if (!u) return undefined
    return { firstName: u.firstName, lastName: u.lastName, email: u.email }
  }, [])

  const bs = useBookingState({ retreat, tier, prefillUser: prefill })
  const {
    state,
    setStep,
    nextStep,
    prevStep,
    setParticipantCount,
    updateParticipant,
    setAnswer,
    setRoommatePreference,
    setRoommateKnownName,
    setPaymentMode,
    setGiftCardCode,
    setCouponCode,
    setDisclosureAccepted,
    validateStep,
    canProceed,
    visibleQuestions,
  } = bs

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Body scroll-lock + Escape to close.
  useEffect(() => {
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = original
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const couponErrorMessage = useCallback(
    (reason: string, minimum?: number): string | null => {
      const c = S.errors.coupon
      const isEvent = retreat.kind === 'event'
      switch (reason) {
        case 'coupon_not_allowed': return isEvent ? c.notAllowedEvent : c.notAllowed
        case 'coupon_unknown': return c.unknown
        case 'coupon_not_started': return c.notStarted
        case 'coupon_expired': return c.expired
        case 'coupon_used_up': return c.usedUp
        case 'coupon_already_used_by_customer': return c.alreadyUsed
        case 'coupon_new_customers_only': return c.newCustomersOnly
        case 'coupon_requires_membership': return c.requiresMembership
        case 'coupon_requires_specific_membership': return c.requiresSpecificMembership
        case 'coupon_wrong_product': return isEvent ? c.wrongProductEvent : c.wrongProduct
        case 'coupon_other_retreat': return isEvent ? c.otherRetreatEvent : c.otherRetreat
        case 'coupon_below_minimum':
          return interp(c.belowMinimum, {
            minimum: formatRetreatPrice(minimum ?? 0, retreat.currency, dateLocale),
          })
        default: return null
      }
    },
    [S, retreat.currency, retreat.kind, dateLocale],
  )

  const skipQuestions = visibleQuestions.length === 0
  const hiddenSteps: StepNum[] = skipQuestions ? [3] : []
  const totalVisibleSteps = skipQuestions ? 3 : 4

  const goNext = useCallback(() => {
    if (validateStep(state.step).length > 0) return
    if (state.step === 2 && skipQuestions) {
      setStep(4)
      return
    }
    nextStep()
  }, [state.step, validateStep, skipQuestions, setStep, nextStep])

  const goPrev = useCallback(() => {
    if (state.step === 4 && skipQuestions) {
      setStep(2)
      return
    }
    prevStep()
  }, [state.step, skipQuestions, setStep, prevStep])

  const finalSummary = useMemo(
    () =>
      computePriceSummary({
        retreat,
        tierPrice: tier.price,
        participantCount: state.participantCount,
        paymentMode: state.paymentMode,
      }),
    [retreat, tier.price, state.participantCount, state.paymentMode],
  )

  const isFree = finalSummary.split.amountNow === 0 && finalSummary.breakdown.totalPrice === 0
  // fejl-justine-retreat: uden Stripe-konto kan der ikke betales online; vinduet siger det
  // i stedet for en knap, der fejler stille. Betalingstrinnet følger serverens regel.
  const paymentBlocked = !isFree && retreat.onlinePaymentAvailable === false
  const lastStepErrors = validateStep(4, { free: isFree })
  const paymentModeMissing = lastStepErrors.includes('errors.paymentModeUnavailable')

  const handleSubmit = useCallback(async () => {
    if (isSubmitting || paymentBlocked) return
    if (validateStep(4, { free: isFree }).length > 0) return
    setSubmitError(null)
    setIsSubmitting(true)

    const body: CreateBookingBody = {
      tierId: tier.id,
      participantCount: state.participantCount,
      participants: state.participants.slice(0, state.participantCount).map((p) => ({
        firstName: p.firstName.trim(),
        lastName: p.lastName.trim(),
        email: p.email.trim(),
        phone: p.phone.trim() || undefined,
        dietaryNotes: p.dietaryNotes.trim() || undefined,
      })),
      customAnswers: state.customAnswers,
      roommatePreference: state.roommatePreference ?? undefined,
      roommateKnownName: state.roommateKnownName.trim() || undefined,
      paymentMode: isFree ? 'free' : state.paymentMode,
      giftCardCode: state.giftCardCode.trim() || undefined,
      couponCode: state.couponCode.trim() || undefined,
      disclosureAccepted: state.disclosureAccepted,
    }

    try {
      // Self-built fetch (not apiClient.post) so we can read the error `details`
      // that carry the coupon rejection reason. Attach a fresh token only when
      // the visitor is logged in — guests book with the API key alone.
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-API-Key': apiClient.getApiKey(),
      }
      if (authStore.isLoggedIn()) {
        const token = await apiClient.getFreshAccessToken()
        if (token) headers['Authorization'] = `Bearer ${token}`
      }

      const res = await fetch(
        `${apiClient.getBaseUrl()}/api/widget/retreats/${retreat.id}/book`,
        { method: 'POST', headers, body: JSON.stringify({ ...body, legalConsent: checkoutConsent(legal, locale) }) },
      )

      if (!res.ok) {
        const betalingsfejl = res.status === 503 || res.status === 502
          ? ((await res.clone().json().catch(() => ({}))) as { error?: string }).error
          : undefined
        if (betalingsfejl === 'payment_not_configured') {
          setSubmitError(S.errors.paymentNotConfigured)
        } else if (betalingsfejl === 'payment_provider_error') {
          setSubmitError(S.errors.paymentProviderError)
        } else if (res.status === 404) {
          setSubmitError(S.errors.notAvailable)
        } else if (res.status === 409) {
          setSubmitError(S.errors.capacityTaken)
        } else if (res.status === 400) {
          let detail: string | null = null
          try {
            const data = (await res.json()) as {
              error?: string
              details?: { reason?: string; minimum?: number }
            }
            if (data.error === 'coupon_invalid' && data.details?.reason) {
              detail = couponErrorMessage(data.details.reason, data.details.minimum)
            } else if (data.error === 'payment_mode_unavailable') {
              detail = S.errors.paymentModeUnavailable
            }
          } catch {
            detail = null
          }
          setSubmitError(detail ?? S.errors.validation)
        } else {
          setSubmitError(S.errors.generic)
        }
        setIsSubmitting(false)
        return
      }

      const data = (await res.json()) as CreateBookingResponse
      if (data.redirectUrl) {
        navigerUd(data.redirectUrl)
        return
      }
      setIsSubmitting(false)
      setSubmitError(S.errors.generic)
    } catch {
      setIsSubmitting(false)
      setSubmitError(S.errors.network)
    }
  }, [
    isSubmitting,
    paymentBlocked,
    validateStep,
    tier.id,
    state,
    isFree,
    retreat.id,
    S,
    couponErrorMessage,
    // Samtykket SKAL med: sætter kunden pakkerejse-fluebenet som det sidste, ville en
    // forældet closure ellers sende købet uden samtykke (0 rækker i member_consents).
    legal,
    locale,
  ])

  const isLastStep = state.step === 4
  const submitLabel = isFree ? S.nav.confirmFree : sLegal.payCard

  const stop = (e: Event) => e.stopPropagation()

  return (
    <div class="hsrb-backdrop" onClick={onClose}>
      <style>{modalStyles}</style>
      <div class="hsrb-dialog" onClick={stop} role="dialog" aria-modal="true" aria-label={S.modalAriaLabel}>
        {/* Header */}
        <div class="hsrb-header">
          <div class="hsrb-header-main">
            <h2 class="hsrb-title">{retreat.title}</h2>
            <StepIndicator
              current={state.step}
              total={4}
              hiddenSteps={hiddenSteps}
              labels={S.steps}
              onSelect={setStep}
            />
          </div>
          <button type="button" class="hsrb-close" onClick={onClose} aria-label={S.nav.cancel}>×</button>
        </div>

        {/* Body */}
        <div class="hsrb-body">
          {state.step === 1 && (
            <WhoStep
              S={S.steps.who}
              tier={tier}
              isEvent={retreat.kind === 'event'}
              participantCount={state.participantCount}
              onParticipantCountChange={setParticipantCount}
              roommatePreference={state.roommatePreference}
              onRoommatePreferenceChange={setRoommatePreference}
              roommateKnownName={state.roommateKnownName}
              onRoommateKnownNameChange={setRoommateKnownName}
            />
          )}
          {state.step === 2 && (
            <InfoStep S={S.steps.info} participants={state.participants} onUpdate={updateParticipant} />
          )}
          {state.step === 3 && (
            <QuestionsStep
              S={S.steps.questions}
              isEvent={retreat.kind === 'event'}
              questions={visibleQuestions}
              answers={state.customAnswers}
              onAnswerChange={setAnswer}
            />
          )}
          {state.step === 4 && (
            <PaymentStep
              timeZone={studietsTidszone(initData.timezone)}
              S={S.steps.payment}
              SP={S.packageTravel}
              retreat={retreat}
              tier={tier}
              participantCount={state.participantCount}
              paymentMode={state.paymentMode}
              onPaymentModeChange={setPaymentMode}
              giftCardCode={state.giftCardCode}
              onGiftCardCodeChange={setGiftCardCode}
              couponCode={state.couponCode}
              onCouponCodeChange={setCouponCode}
              disclosureAccepted={state.disclosureAccepted}
              onDisclosureAcceptedChange={setDisclosureAccepted}
              locale={dateLocale}
              submitError={submitError}
              isSubmitting={isSubmitting}
            />
          )}
        </div>

        {/* Footer */}
        <div class="hsrb-footer">
          {(paymentBlocked || (isLastStep && paymentModeMissing && !isFree)) && (
            <p class="hsrb-error-text" role="alert" data-testid="retreat-payment-unavailable">
              {paymentBlocked ? S.errors.paymentNotConfigured : S.errors.paymentModeUnavailable}
            </p>
          )}
          {isLastStep && (
            <CheckoutLegal
              s={sLegal}
              initData={initData}
              noWithdrawalRight
              free={isFree}
              state={legal}
              onChange={setLegal}
              disabled={isSubmitting}
            />
          )}
          <PriceSummary
            S={S.priceSummary}
            retreat={retreat}
            tier={tier}
            participantCount={state.participantCount}
            paymentMode={state.paymentMode}
            locale={dateLocale}
          />
          <div class="hsrb-actions">
            <button
              type="button"
              class="hsrb-btn-secondary"
              onClick={state.step === 1 ? onClose : goPrev}
              disabled={isSubmitting}
            >
              {state.step === 1 ? S.nav.cancel : S.nav.back}
            </button>
            {!isLastStep ? (
              <button
                type="button"
                class="hsrb-btn-primary"
                onClick={goNext}
                disabled={!canProceed || isSubmitting}
              >
                {S.nav.next}
              </button>
            ) : (
              <button
                type="button"
                class="hsrb-btn-primary"
                onClick={handleSubmit}
                disabled={isSubmitting || lastStepErrors.length > 0 || !legal.termsAccepted || paymentBlocked}
              >
                {isSubmitting ? S.nav.submitting : submitLabel}
              </button>
            )}
          </div>
          <p class="hsrb-progress">
            {interp(S.nav.stepProgress, {
              current: visibleStepIndex(state.step, hiddenSteps),
              total: totalVisibleSteps,
            })}
          </p>
        </div>
      </div>
    </div>
  )
}

function visibleStepIndex(step: StepNum, hidden: StepNum[]): number {
  let count = 0
  for (let i = 1; i <= step; i++) {
    if (!hidden.includes(i as StepNum)) count++
  }
  return count
}

// ============================================================================
// Step indicator
// ============================================================================
function StepIndicator({
  current,
  total,
  hiddenSteps,
  labels,
  onSelect,
}: {
  current: StepNum
  total: number
  hiddenSteps: StepNum[]
  labels: RetreatBookingStrings['steps']
  onSelect: (s: StepNum) => void
}) {
  const shortLabels: Record<number, string> = {
    1: labels.who.shortLabel,
    2: labels.info.shortLabel,
    3: labels.questions.shortLabel,
    4: labels.payment.shortLabel,
  }
  const steps = Array.from({ length: total }, (_, i) => (i + 1) as StepNum).filter(
    (s) => !hiddenSteps.includes(s),
  )
  return (
    <nav class="hsrb-steps" aria-label={labels.indicatorAriaLabel}>
      {steps.map((step, idx) => {
        const isActive = step === current
        const isCompleted = step < current
        const clickable = isCompleted
        return (
          <div key={step} class="hsrb-step-wrap">
            <button
              type="button"
              disabled={!clickable}
              onClick={clickable ? () => onSelect(step) : undefined}
              aria-current={isActive ? 'step' : undefined}
              aria-label={`${shortLabels[step]} (${step}/${total})`}
              class={`hsrb-step${isActive ? ' hsrb-step-active' : isCompleted ? ' hsrb-step-done' : ''}`}
            >
              {isCompleted ? '✓' : step}
            </button>
            {idx < steps.length - 1 && (
              <span class={`hsrb-step-line${step < current ? ' hsrb-step-line-done' : ''}`} aria-hidden="true" />
            )}
          </div>
        )
      })}
    </nav>
  )
}

// ============================================================================
// Step 1 — who
// ============================================================================
function WhoStep({
  S,
  tier,
  isEvent,
  participantCount,
  onParticipantCountChange,
  roommatePreference,
  onRoommatePreferenceChange,
  roommateKnownName,
  onRoommateKnownNameChange,
}: {
  S: RetreatBookingStrings['steps']['who']
  tier: RetreatTier
  /** Et event har ingen værelser: ingen værelseskammerat og intet dobbeltværelse. */
  isEvent: boolean
  participantCount: 1 | 2
  onParticipantCountChange: (c: 1 | 2) => void
  roommatePreference: RoommatePreference | null
  onRoommatePreferenceChange: (v: RoommatePreference) => void
  roommateKnownName: string
  onRoommateKnownNameChange: (v: string) => void
}) {
  const allowsPair = tier.spotsPerBookingMax >= 2
  const minIsPair = tier.spotsPerBookingMin >= 2
  const showRoommate = !isEvent && tier.occupancy === 'shared_double' && participantCount === 1
  const showPrivateSolo = !isEvent && tier.occupancy === 'private_double' && participantCount === 1

  return (
    <div class="hsrb-step-content">
      <div>
        <h3 class="hsrb-step-title">{S.title}</h3>
        <p class="hsrb-step-desc">{S.description}</p>
      </div>

      {allowsPair && (
        <fieldset class="hsrb-fieldset">
          <legend class="hsrb-legend">{S.countLegend}</legend>
          <div class="hsrb-choice-grid">
            {!minIsPair && (
              <button
                type="button"
                onClick={() => onParticipantCountChange(1)}
                aria-pressed={participantCount === 1}
                class={`hsrb-choice${participantCount === 1 ? ' hsrb-choice-active' : ''}`}
              >
                <div class="hsrb-choice-name">{S.soloOption}</div>
                <div class="hsrb-choice-help">{S.soloHelp}</div>
              </button>
            )}
            <button
              type="button"
              onClick={() => onParticipantCountChange(2)}
              aria-pressed={participantCount === 2}
              class={`hsrb-choice${participantCount === 2 ? ' hsrb-choice-active' : ''}`}
            >
              <div class="hsrb-choice-name">{S.pairOption}</div>
              <div class="hsrb-choice-help">{S.pairHelp}</div>
            </button>
          </div>
        </fieldset>
      )}

      {showPrivateSolo && (
        <div class="hsrb-info-box">
          <p class="hsrb-info-title">{S.privateDoubleSoloTitle}</p>
          <p class="hsrb-info-body">{S.privateDoubleSoloBody}</p>
        </div>
      )}

      {showRoommate && (
        <fieldset class="hsrb-fieldset">
          <legend class="hsrb-legend">{S.roommateLegend}</legend>
          <p class="hsrb-step-desc">{S.sharedDoubleSoloHelp}</p>
          <div class="hsrb-radio-list">
            {(['match_me', 'alone', 'known_partner'] as RoommatePreference[]).map((opt) => (
              <label key={opt} class={`hsrb-radio${roommatePreference === opt ? ' hsrb-radio-active' : ''}`}>
                <input
                  type="radio"
                  name="hsrb-roommate"
                  value={opt}
                  checked={roommatePreference === opt}
                  onChange={() => onRoommatePreferenceChange(opt)}
                />
                <span class="hsrb-radio-body">
                  <span class="hsrb-radio-name">
                    {opt === 'match_me' ? S.roommate.matchMe : opt === 'alone' ? S.roommate.alone : S.roommate.knownPartner}
                  </span>
                  <span class="hsrb-radio-help">
                    {opt === 'match_me' ? S.roommate.matchMeHelp : opt === 'alone' ? S.roommate.aloneHelp : S.roommate.knownPartnerHelp}
                  </span>
                </span>
              </label>
            ))}
            {roommatePreference === 'known_partner' && (
              <div class="hsrb-known-name">
                <label class="hsrb-field-label">{S.roommate.knownPartnerNameLabel}</label>
                <input
                  type="text"
                  class="hsrb-input"
                  value={roommateKnownName}
                  onInput={(e) => onRoommateKnownNameChange((e.target as HTMLInputElement).value)}
                  placeholder={S.roommate.knownPartnerNamePlaceholder}
                  maxLength={200}
                />
              </div>
            )}
          </div>
        </fieldset>
      )}
    </div>
  )
}

// ============================================================================
// Step 2 — participant info
// ============================================================================
function InfoStep({
  S,
  participants,
  onUpdate,
}: {
  S: RetreatBookingStrings['steps']['info']
  participants: ParticipantDraft[]
  onUpdate: (index: number, fields: Partial<ParticipantDraft>) => void
}) {
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const key = (idx: number, f: ParticipantField) => `${idx}.${f}`
  const blur = (idx: number, f: ParticipantField) =>
    setTouched((prev) => ({ ...prev, [key(idx, f)]: true }))

  const fieldError = (idx: number, f: ParticipantField): string | null => {
    if (!touched[key(idx, f)]) return null
    if (!invalidParticipantFields(participants[idx]).has(f)) return null
    if (f === 'email') return S.errors.invalidEmail
    if (f === 'firstName') return S.errors.firstNameRequired
    if (f === 'lastName') return S.errors.lastNameRequired
    if (f === 'phone') return S.errors.invalidPhone
    return S.errors.dietaryTooLong
  }

  const MAX_DIETARY = 500

  return (
    <div class="hsrb-step-content">
      <div>
        <h3 class="hsrb-step-title">{S.title}</h3>
        <p class="hsrb-step-desc">{S.description}</p>
      </div>

      {participants.map((p, idx) => {
        const heading =
          participants.length > 1
            ? interp(S.participantHeading, { index: idx + 1 })
            : S.participantHeadingSingle
        const firstErr = fieldError(idx, 'firstName')
        const lastErr = fieldError(idx, 'lastName')
        const emailErr = fieldError(idx, 'email')
        const phoneErr = fieldError(idx, 'phone')
        const dietErr = fieldError(idx, 'dietaryNotes')
        return (
          <section key={idx} class="hsrb-participant">
            <h4 class="hsrb-participant-heading">{heading}</h4>
            <div class="hsrb-field-grid">
              <div class="hsrb-field">
                <label class="hsrb-field-label">{S.firstNameLabel} <span class="hsrb-req">*</span></label>
                <input
                  type="text"
                  class={`hsrb-input${firstErr ? ' hsrb-input-error' : ''}`}
                  value={p.firstName}
                  onInput={(e) => onUpdate(idx, { firstName: (e.target as HTMLInputElement).value })}
                  onBlur={() => blur(idx, 'firstName')}
                  maxLength={100}
                />
                {firstErr && <p class="hsrb-error-text">{firstErr}</p>}
              </div>
              <div class="hsrb-field">
                <label class="hsrb-field-label">{S.lastNameLabel} <span class="hsrb-req">*</span></label>
                <input
                  type="text"
                  class={`hsrb-input${lastErr ? ' hsrb-input-error' : ''}`}
                  value={p.lastName}
                  onInput={(e) => onUpdate(idx, { lastName: (e.target as HTMLInputElement).value })}
                  onBlur={() => blur(idx, 'lastName')}
                  maxLength={100}
                />
                {lastErr && <p class="hsrb-error-text">{lastErr}</p>}
              </div>
            </div>
            <div class="hsrb-field">
              <label class="hsrb-field-label">{S.emailLabel} <span class="hsrb-req">*</span></label>
              <input
                type="email"
                class={`hsrb-input${emailErr ? ' hsrb-input-error' : ''}`}
                value={p.email}
                onInput={(e) => onUpdate(idx, { email: (e.target as HTMLInputElement).value })}
                onBlur={() => blur(idx, 'email')}
                maxLength={255}
                autocomplete="email"
              />
              {emailErr && <p class="hsrb-error-text">{emailErr}</p>}
            </div>
            <div class="hsrb-field">
              <label class="hsrb-field-label">{S.phoneLabel}</label>
              <input
                type="tel"
                class={`hsrb-input${phoneErr ? ' hsrb-input-error' : ''}`}
                value={p.phone}
                onInput={(e) => onUpdate(idx, { phone: (e.target as HTMLInputElement).value })}
                onBlur={() => blur(idx, 'phone')}
                maxLength={20}
                autocomplete="tel"
              />
              {phoneErr && <p class="hsrb-error-text">{phoneErr}</p>}
            </div>
            <div class="hsrb-field">
              <label class="hsrb-field-label">{S.dietaryLabel}</label>
              <textarea
                class={`hsrb-input hsrb-textarea${dietErr ? ' hsrb-input-error' : ''}`}
                value={p.dietaryNotes}
                onInput={(e) => onUpdate(idx, { dietaryNotes: (e.target as HTMLTextAreaElement).value })}
                onBlur={() => blur(idx, 'dietaryNotes')}
                maxLength={MAX_DIETARY}
                rows={3}
                placeholder={S.dietaryPlaceholder}
              />
              <div class="hsrb-field-foot">
                <span class="hsrb-field-help">{dietErr ?? S.dietaryHelp}</span>
                <span class="hsrb-counter">{p.dietaryNotes.length}/{MAX_DIETARY}</span>
              </div>
            </div>
          </section>
        )
      })}
    </div>
  )
}

// ============================================================================
// Step 3 — custom questions
// ============================================================================
function QuestionsStep({
  S,
  isEvent,
  questions,
  answers,
  onAnswerChange,
}: {
  S: RetreatBookingStrings['steps']['questions']
  isEvent: boolean
  questions: CustomQuestion[]
  answers: Record<string, string>
  onAnswerChange: (key: string, value: string) => void
}) {
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const mark = (k: string) => setTouched((prev) => ({ ...prev, [k]: true }))

  if (questions.length === 0) {
    return <div class="hsrb-step-content"><p class="hsrb-step-desc">{S.noQuestions}</p></div>
  }

  return (
    <div class="hsrb-step-content">
      <div>
        <h3 class="hsrb-step-title">{S.title}</h3>
        <p class="hsrb-step-desc">{isEvent ? S.descriptionEvent : S.description}</p>
      </div>
      {questions.map((q) => {
        const value = answers[q.key] ?? ''
        const showError = q.required && (touched[q.key] ?? false) && value.trim().length === 0
        const maxLength = q.maxLength ?? 500
        return (
          <div key={q.key} class="hsrb-field">
            <label class="hsrb-field-label">
              {q.label}{q.required && <span class="hsrb-req"> *</span>}
            </label>
            {q.type === 'text' && (
              <input
                type="text"
                class={`hsrb-input${showError ? ' hsrb-input-error' : ''}`}
                value={value}
                onInput={(e) => onAnswerChange(q.key, (e.target as HTMLInputElement).value)}
                onBlur={() => mark(q.key)}
                maxLength={maxLength}
              />
            )}
            {q.type === 'textarea' && (
              <textarea
                class={`hsrb-input hsrb-textarea${showError ? ' hsrb-input-error' : ''}`}
                value={value}
                onInput={(e) => onAnswerChange(q.key, (e.target as HTMLTextAreaElement).value)}
                onBlur={() => mark(q.key)}
                maxLength={maxLength}
                rows={3}
              />
            )}
            {q.type === 'select' && q.options && q.options.length > 0 && (
              <select
                class={`hsrb-input${showError ? ' hsrb-input-error' : ''}`}
                value={value}
                onChange={(e) => onAnswerChange(q.key, (e.target as HTMLSelectElement).value)}
                onBlur={() => mark(q.key)}
              >
                <option value="">{S.selectPlaceholder}</option>
                {q.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            )}
            {showError && <p class="hsrb-error-text">{S.errors.required}</p>}
          </div>
        )
      })}
    </div>
  )
}

// ============================================================================
// Step 4 — payment
// ============================================================================
// Fristen i studiets tidszone, ikke den besøgendes browser (QA 27-10).
function formatDeadline(iso: string | null, locale: string, timeZone: string): string {
  return fmtFrist(iso, locale, timeZone)
}

function PaymentStep({
  S,
  SP,
  retreat,
  tier,
  participantCount,
  paymentMode,
  onPaymentModeChange,
  giftCardCode,
  onGiftCardCodeChange,
  couponCode,
  onCouponCodeChange,
  disclosureAccepted,
  onDisclosureAcceptedChange,
  locale,
  timeZone,
  submitError,
  isSubmitting,
}: {
  S: RetreatBookingStrings['steps']['payment']
  SP: RetreatBookingStrings['packageTravel']
  retreat: RetreatData
  tier: RetreatTier
  participantCount: 1 | 2
  paymentMode: PaymentMode
  onPaymentModeChange: (m: PaymentMode) => void
  giftCardCode: string
  onGiftCardCodeChange: (v: string) => void
  couponCode: string
  onCouponCodeChange: (v: string) => void
  disclosureAccepted: boolean
  onDisclosureAcceptedChange: (v: boolean) => void
  locale: string
  /** Studiets tidszone (initData.timezone) til depositum-fristen. */
  timeZone: string
  submitError: string | null
  isSubmitting: boolean
}) {
  const fmt = (n: number) => formatRetreatPrice(n, retreat.currency, locale)

  const full = computePriceSummary({ retreat, tierPrice: tier.price, participantCount, paymentMode: 'full' })
  const deposit =
    retreat.depositPercent && retreat.depositPercent > 0
      ? computePriceSummary({ retreat, tierPrice: tier.price, participantCount, paymentMode: 'deposit' })
      : null
  const installments =
    retreat.installmentsEnabled && retreat.installmentsCount
      ? computePriceSummary({ retreat, tierPrice: tier.price, participantCount, paymentMode: 'installments' })
      : null

  return (
    <div class="hsrb-step-content">
      <div>
        <h3 class="hsrb-step-title">{S.title}</h3>
        <p class="hsrb-step-desc">{S.description}</p>
      </div>

      <div class="hsrb-breakdown">
        <div class="hsrb-breakdown-row">
          <span>{interp(S.breakdownLine, { count: participantCount, tierName: tier.name, perPerson: fmt(tier.price) })}</span>
          <span class="hsrb-num">{fmt(full.breakdown.subtotal)}</span>
        </div>
        {!retreat.vatExempt && full.breakdown.vatAmount > 0 && (
          <div class="hsrb-breakdown-row hsrb-muted">
            <span>{interp(S.vatLine, { rate: retreat.vatRate })}</span>
            <span class="hsrb-num">{fmt(full.breakdown.vatAmount)}</span>
          </div>
        )}
        <div class="hsrb-breakdown-row hsrb-breakdown-total">
          <span>{S.totalLabel}</span>
          <span class="hsrb-num">{fmt(full.breakdown.totalPrice)}</span>
        </div>
      </div>

      {/* Et event betales altid fuldt (V524): intet valg af depositum eller rater. */}
      {retreat.kind !== 'event' && (
      <fieldset class="hsrb-fieldset">
        <legend class="hsrb-legend">{S.chooseModeLegend}</legend>
        <div class="hsrb-radio-list">
          {retreat.allowFullPayment && (
            <label class={`hsrb-pay${paymentMode === 'full' ? ' hsrb-pay-active' : ''}`}>
              <input type="radio" name="hsrb-pay" checked={paymentMode === 'full'} onChange={() => onPaymentModeChange('full')} class="hsrb-sr" />
              <span class="hsrb-pay-body">
                <span class="hsrb-pay-name">{S.fullPayment}</span>
                <span class="hsrb-pay-help">{S.fullPaymentHelp}</span>
              </span>
              <span class="hsrb-pay-amount">{fmt(full.split.amountNow)}</span>
            </label>
          )}
          {deposit && (
            <label class={`hsrb-pay${paymentMode === 'deposit' ? ' hsrb-pay-active' : ''}`}>
              <input type="radio" name="hsrb-pay" checked={paymentMode === 'deposit'} onChange={() => onPaymentModeChange('deposit')} class="hsrb-sr" />
              <span class="hsrb-pay-body">
                <span class="hsrb-pay-name">{S.depositPayment}</span>
                <span class="hsrb-pay-help">
                  {retreat.depositDueDate
                    ? interp(S.depositPaymentHelpWithDate, {
                        percent: retreat.depositPercent ?? 0,
                        remaining: fmt(deposit.split.amountDueLater),
                        dueDate: formatDeadline(retreat.depositDueDate, locale, timeZone),
                      })
                    : interp(S.depositPaymentHelp, {
                        percent: retreat.depositPercent ?? 0,
                        remaining: fmt(deposit.split.amountDueLater),
                      })}
                </span>
              </span>
              <span class="hsrb-pay-amount">{fmt(deposit.split.amountNow)}</span>
            </label>
          )}
          {installments && retreat.installmentsCount && (
            <label class={`hsrb-pay${paymentMode === 'installments' ? ' hsrb-pay-active' : ''}`}>
              <input type="radio" name="hsrb-pay" checked={paymentMode === 'installments'} onChange={() => onPaymentModeChange('installments')} class="hsrb-sr" />
              <span class="hsrb-pay-body">
                <span class="hsrb-pay-name">{interp(S.installmentsPayment, {})}</span>
                <span class="hsrb-pay-help">
                  {interp(S.installmentsPaymentHelp, {
                    count: retreat.installmentsCount,
                    perInstallment: fmt(installments.split.amountNow),
                  })}
                </span>
              </span>
              <span class="hsrb-pay-amount">{fmt(installments.split.amountNow)}</span>
            </label>
          )}
        </div>
      </fieldset>
      )}

      {(retreat.allowGiftCards || retreat.allowCoupons) && (
        <div class="hsrb-step-content">
          <h4 class="hsrb-field-label">{S.discountSectionTitle}</h4>
          {retreat.allowGiftCards && (
            <div class="hsrb-field">
              <label class="hsrb-field-label">{S.giftCardLabel}</label>
              <input
                type="text"
                class="hsrb-input hsrb-upper"
                value={giftCardCode}
                onInput={(e) => onGiftCardCodeChange((e.target as HTMLInputElement).value)}
                placeholder={S.giftCardPlaceholder}
                maxLength={50}
              />
              <p class="hsrb-field-help">{S.giftCardHelp}</p>
            </div>
          )}
          {retreat.allowCoupons && (
            <div class="hsrb-field">
              <label class="hsrb-field-label">{S.couponLabel}</label>
              <input
                type="text"
                class="hsrb-input hsrb-upper"
                value={couponCode}
                onInput={(e) => onCouponCodeChange((e.target as HTMLInputElement).value)}
                placeholder={S.couponPlaceholder}
                maxLength={50}
              />
              <p class="hsrb-field-help">{S.couponHelp}</p>
            </div>
          )}
        </div>
      )}

      {retreat.packageTravel && (
        <div class="hsrb-step-content">
          <PackageTravelDisclosure S={SP} packageTravel={retreat.packageTravel} />
          <label class="hsrb-consent">
            <input
              type="checkbox"
              checked={disclosureAccepted}
              onChange={(e) => onDisclosureAcceptedChange((e.target as HTMLInputElement).checked)}
            />
            <span>{SP.consentLabel}</span>
          </label>
        </div>
      )}

      {submitError && <div class="hsrb-alert-error" role="alert">{submitError}</div>}
      {isSubmitting && <div class="hsrb-alert-info" role="status">{S.submitting}</div>}
    </div>
  )
}

function PackageTravelDisclosure({
  S,
  packageTravel,
}: {
  S: RetreatBookingStrings['packageTravel']
  packageTravel: RetreatPackageTravel
}) {
  const organizer = packageTravel.organizerName
  const memberNumber = packageTravel.rgfMemberNumber
  return (
    <div class="hsrb-package">
      <div class="hsrb-package-head">
        {packageTravel.rgfLogoUrl && (
          <img src={packageTravel.rgfLogoUrl} alt={S.logoAlt} class="hsrb-package-logo" />
        )}
        <div>
          <p class="hsrb-package-title">{S.heading}</p>
          <p>{interp(S.organizerNote, { organizer })}</p>
          <p>{interp(S.registeredLine, { organizer, memberNumber })}</p>
        </div>
      </div>
      <details class="hsrb-package-details">
        <summary class="hsrb-package-toggle">{S.formToggle}</summary>
        <div class="hsrb-package-form">
          <p>{S.formIntro}</p>
          <p>{interp(S.formResponsibility, { organizer, memberNumber })}</p>
          <p class="hsrb-package-rights-title">{S.rightsTitle}</p>
          <ul class="hsrb-package-rights">
            <li>{S.right1}</li>
            <li>{S.right2}</li>
            <li>{S.right3}</li>
            <li>{S.right4}</li>
            <li>{S.right5}</li>
            <li>{S.right6}</li>
          </ul>
          <p class="hsrb-package-contact">{S.contact}</p>
        </div>
      </details>
    </div>
  )
}

// ============================================================================
// Price summary (footer)
// ============================================================================
function PriceSummary({
  S,
  retreat,
  tier,
  participantCount,
  paymentMode,
  locale,
}: {
  S: RetreatBookingStrings['priceSummary']
  retreat: RetreatData
  tier: RetreatTier
  participantCount: 1 | 2
  paymentMode: PaymentMode
  locale: string
}) {
  const { breakdown, split } = computePriceSummary({ retreat, tierPrice: tier.price, participantCount, paymentMode })
  const fmt = (n: number) => formatRetreatPrice(n, retreat.currency, locale)

  const splitNote =
    paymentMode === 'deposit'
      ? interp(S.depositNote, { percent: retreat.depositPercent ?? 0 })
      : paymentMode === 'installments' && retreat.installmentsCount
        ? interp(S.installmentsNote, { count: retreat.installmentsCount })
        : null

  return (
    <div class="hsrb-summary">
      <div class="hsrb-summary-row">
        <span class="hsrb-muted">{interp(S.lineLabel, { count: participantCount, tierName: tier.name })}</span>
        <span class="hsrb-num">{fmt(breakdown.subtotal)}</span>
      </div>
      {!retreat.vatExempt && breakdown.vatAmount > 0 && (
        <div class="hsrb-summary-row hsrb-muted hsrb-small">
          <span>{interp(S.vatLabel, { rate: retreat.vatRate })}</span>
          <span class="hsrb-num">{fmt(breakdown.vatAmount)}</span>
        </div>
      )}
      <div class="hsrb-summary-row hsrb-summary-total">
        <span>{S.totalLabel}</span>
        <span class="hsrb-num">{fmt(breakdown.totalPrice)}</span>
      </div>
      {split.amountDueLater > 0 && (
        <div class="hsrb-summary-split">
          {splitNote && <p class="hsrb-split-note">{splitNote}</p>}
          <div class="hsrb-summary-row hsrb-small">
            <span class="hsrb-muted">{S.payNowLabel}</span>
            <span class="hsrb-num">{fmt(split.amountNow)}</span>
          </div>
          <div class="hsrb-summary-row hsrb-small">
            <span class="hsrb-muted">{S.dueLaterLabel}</span>
            <span class="hsrb-num">{fmt(split.amountDueLater)}</span>
          </div>
        </div>
      )}
    </div>
  )
}

const modalStyles = `
  .hsrb-backdrop {
    position: fixed; inset: 0; background: rgba(0,0,0,0.5);
    display: flex; align-items: flex-end; justify-content: center;
    z-index: 100000; animation: hsrb-fade .15s ease-out;
  }
  @media (min-width: 640px) { .hsrb-backdrop { align-items: center; padding: 1rem; } }
  @keyframes hsrb-fade { from { opacity: 0; } to { opacity: 1; } }
  .hsrb-dialog {
    position: relative; background: var(--yoga-card-bg, #fff);
    width: 100%; max-height: 95vh; display: flex; flex-direction: column;
    border-radius: 16px 16px 0 0; box-shadow: 0 20px 60px rgba(0,0,0,0.25);
    color: var(--yoga-text, #0a0a0a); box-sizing: border-box; overflow: hidden;
  }
  @media (min-width: 640px) {
    .hsrb-dialog { width: 90vw; min-width: 600px; max-width: 700px; max-height: 90vh; border-radius: 16px; }
  }
  .hsrb-header {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 0.75rem;
    padding: 1rem 1.25rem; border-bottom: 1px solid var(--yoga-card-border, #e5e7eb); flex-shrink: 0;
  }
  @media (min-width: 640px) { .hsrb-header { padding: 1.25rem 1.5rem; } }
  .hsrb-header-main { flex: 1; min-width: 0; }
  .hsrb-title { font-size: 1.05rem; font-weight: 700; margin: 0; line-height: 1.25; }
  @media (min-width: 640px) { .hsrb-title { font-size: 1.2rem; } }
  .hsrb-close {
    flex-shrink: 0; background: transparent; border: 0; font-size: 1.6rem; line-height: 1;
    color: var(--yoga-text-muted, #737373); cursor: pointer; padding: 0 0.4rem;
  }
  .hsrb-close:hover { color: var(--yoga-text, #0a0a0a); }
  .hsrb-body { flex: 1; overflow-y: auto; padding: 1.25rem; }
  @media (min-width: 640px) { .hsrb-body { padding: 1.5rem; } }
  .hsrb-footer {
    flex-shrink: 0; border-top: 1px solid var(--yoga-card-border, #e5e7eb);
    background: var(--yoga-card-bg, #fff); padding: 1rem 1.25rem;
    display: flex; flex-direction: column; gap: 0.75rem;
  }
  @media (min-width: 640px) { .hsrb-footer { padding: 1.25rem 1.5rem; } }

  /* Step indicator */
  .hsrb-steps { display: flex; align-items: center; gap: 0.35rem; margin-top: 0.6rem; }
  .hsrb-step-wrap { display: flex; align-items: center; gap: 0.35rem; }
  .hsrb-step {
    width: 28px; height: 28px; border-radius: 999px; border: 0; cursor: default;
    font-size: 0.8rem; font-weight: 700; display: flex; align-items: center; justify-content: center;
    background: #f0f0f0; color: #a3a3a3; transition: all .2s;
  }
  .hsrb-step-active { background: var(--yoga-primary, #FF6B35); color: #fff; }
  .hsrb-step-done { background: rgba(0,0,0,0.06); color: var(--yoga-text, #0a0a0a); cursor: pointer; }
  .hsrb-step-line { display: block; width: 16px; height: 1px; background: #e5e7eb; }
  .hsrb-step-line-done { background: var(--yoga-primary, #FF6B35); opacity: 0.5; }

  /* Steps */
  .hsrb-step-content { display: flex; flex-direction: column; gap: 1.25rem; }
  .hsrb-step-title { font-size: 1.1rem; font-weight: 700; margin: 0; }
  .hsrb-step-desc { font-size: 0.9rem; color: var(--yoga-text-muted, #525252); margin: 0.25rem 0 0; line-height: 1.5; }
  .hsrb-fieldset { border: 0; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.75rem; }
  .hsrb-legend { font-size: 0.85rem; font-weight: 600; color: var(--yoga-text, #404040); padding: 0; }
  .hsrb-choice-grid { display: grid; grid-template-columns: 1fr; gap: 0.75rem; }
  @media (min-width: 480px) { .hsrb-choice-grid { grid-template-columns: 1fr 1fr; } }
  .hsrb-choice {
    border: 2px solid var(--yoga-card-border, #e5e7eb); border-radius: 12px; padding: 1rem;
    text-align: left; background: var(--yoga-card-bg, #fff); cursor: pointer; transition: all .15s;
  }
  .hsrb-choice:hover { border-color: rgba(0,0,0,0.25); }
  .hsrb-choice-active { border-color: var(--yoga-primary, #FF6B35); background: rgba(255,107,53,0.06); }
  .hsrb-choice-name { font-weight: 600; }
  .hsrb-choice-help { font-size: 0.78rem; color: var(--yoga-text-muted, #737373); margin-top: 0.25rem; }
  .hsrb-info-box { border: 1px solid #bfdbfe; background: #eff6ff; border-radius: 12px; padding: 1rem; }
  .hsrb-info-title { font-weight: 600; margin: 0; color: #1e3a8a; }
  .hsrb-info-body { margin: 0.25rem 0 0; font-size: 0.88rem; color: #1e40af; }
  .hsrb-radio-list { display: flex; flex-direction: column; gap: 0.5rem; }
  .hsrb-radio {
    display: flex; align-items: flex-start; gap: 0.75rem; border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: 10px; padding: 0.75rem; cursor: pointer; transition: all .15s;
  }
  .hsrb-radio:hover { background: rgba(0,0,0,0.02); }
  .hsrb-radio-active { border-color: var(--yoga-primary, #FF6B35); background: rgba(255,107,53,0.06); }
  .hsrb-radio-body { display: flex; flex-direction: column; }
  .hsrb-radio-name { font-size: 0.9rem; font-weight: 600; }
  .hsrb-radio-help { font-size: 0.78rem; color: var(--yoga-text-muted, #737373); }
  .hsrb-known-name { margin-left: 1.75rem; }

  /* Fields */
  .hsrb-participant { border: 1px solid var(--yoga-card-border, #e5e7eb); border-radius: 12px; padding: 1rem; display: flex; flex-direction: column; gap: 0.85rem; }
  .hsrb-participant-heading { font-size: 0.9rem; font-weight: 700; margin: 0; }
  .hsrb-field-grid { display: grid; grid-template-columns: 1fr; gap: 0.75rem; }
  @media (min-width: 480px) { .hsrb-field-grid { grid-template-columns: 1fr 1fr; } }
  .hsrb-field { display: flex; flex-direction: column; }
  .hsrb-field-label { font-size: 0.8rem; font-weight: 600; color: var(--yoga-text, #404040); margin-bottom: 0.3rem; }
  .hsrb-req { color: #dc2626; }
  .hsrb-input {
    width: 100%; box-sizing: border-box; border: 1px solid var(--yoga-card-border, #d4d4d4);
    border-radius: 8px; padding: 0.55rem 0.75rem; font-size: 0.9rem; font-family: inherit;
    background: var(--yoga-card-bg, #fff); color: var(--yoga-text, #0a0a0a);
  }
  .hsrb-input:focus { outline: none; border-color: var(--yoga-primary, #FF6B35); box-shadow: 0 0 0 1px var(--yoga-primary, #FF6B35); }
  .hsrb-input-error { border-color: #fca5a5; }
  .hsrb-input-error:focus { border-color: #ef4444; box-shadow: 0 0 0 1px #ef4444; }
  .hsrb-textarea { resize: vertical; }
  .hsrb-upper { text-transform: uppercase; }
  .hsrb-error-text { font-size: 0.78rem; color: #dc2626; margin: 0.3rem 0 0; }
  .hsrb-field-foot { display: flex; justify-content: space-between; margin-top: 0.3rem; }
  .hsrb-field-help { font-size: 0.78rem; color: var(--yoga-text-muted, #737373); }
  .hsrb-counter { font-size: 0.75rem; color: #a3a3a3; }

  /* Payment breakdown */
  .hsrb-breakdown { border: 1px solid var(--yoga-card-border, #e5e7eb); background: rgba(0,0,0,0.02); border-radius: 12px; padding: 1rem; display: flex; flex-direction: column; gap: 0.5rem; }
  .hsrb-breakdown-row { display: flex; justify-content: space-between; font-size: 0.9rem; }
  .hsrb-breakdown-total { border-top: 1px solid var(--yoga-card-border, #e5e7eb); padding-top: 0.5rem; font-weight: 700; }
  .hsrb-muted { color: var(--yoga-text-muted, #737373); }
  .hsrb-small { font-size: 0.8rem; }
  .hsrb-num { font-variant-numeric: tabular-nums; }
  .hsrb-pay {
    display: flex; align-items: flex-start; gap: 0.75rem; border: 2px solid var(--yoga-card-border, #e5e7eb);
    border-radius: 12px; padding: 1rem; cursor: pointer; transition: all .15s;
  }
  .hsrb-pay:hover { border-color: rgba(0,0,0,0.25); }
  .hsrb-pay-active { border-color: var(--yoga-primary, #FF6B35); background: rgba(255,107,53,0.06); }
  .hsrb-pay-body { flex: 1; display: flex; flex-direction: column; }
  .hsrb-pay-name { font-weight: 600; }
  .hsrb-pay-help { font-size: 0.78rem; color: var(--yoga-text-muted, #737373); margin-top: 0.2rem; }
  .hsrb-pay-amount { font-weight: 700; font-variant-numeric: tabular-nums; }
  .hsrb-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); border: 0; }

  /* Package travel */
  .hsrb-package { border: 1px solid var(--yoga-card-border, #e5e7eb); background: rgba(0,0,0,0.02); border-radius: 12px; padding: 1rem; font-size: 0.85rem; color: var(--yoga-text-muted, #525252); }
  .hsrb-package-head { display: flex; align-items: flex-start; gap: 0.75rem; }
  .hsrb-package-logo { height: 44px; width: auto; flex-shrink: 0; background: #fff; padding: 4px; border-radius: 6px; border: 1px solid #e5e7eb; }
  .hsrb-package-title { font-weight: 700; color: var(--yoga-text, #0a0a0a); margin: 0 0 0.25rem; }
  .hsrb-package-head p { margin: 0 0 0.2rem; }
  .hsrb-package-details { margin-top: 0.75rem; }
  .hsrb-package-toggle { cursor: pointer; color: var(--yoga-primary, #FF6B35); font-weight: 600; }
  .hsrb-package-form { margin-top: 0.5rem; display: flex; flex-direction: column; gap: 0.5rem; }
  .hsrb-package-rights-title { font-weight: 600; color: var(--yoga-text, #404040); }
  .hsrb-package-rights { margin: 0; padding-left: 1.25rem; display: flex; flex-direction: column; gap: 0.25rem; }
  .hsrb-package-contact { font-size: 0.78rem; color: #a3a3a3; }
  .hsrb-consent { display: flex; align-items: flex-start; gap: 0.6rem; cursor: pointer; font-size: 0.88rem; }

  /* Alerts */
  .hsrb-alert-error { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; border-radius: 10px; padding: 0.75rem 0.9rem; font-size: 0.85rem; }
  .hsrb-alert-info { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; border-radius: 10px; padding: 0.75rem 0.9rem; font-size: 0.85rem; }

  /* Footer summary + actions */
  .hsrb-summary { display: flex; flex-direction: column; gap: 0.35rem; }
  .hsrb-summary-row { display: flex; justify-content: space-between; font-size: 0.9rem; }
  .hsrb-summary-total { font-weight: 700; border-top: 1px solid var(--yoga-card-border, #e5e7eb); padding-top: 0.35rem; }
  .hsrb-summary-split { background: rgba(0,0,0,0.02); border: 1px solid var(--yoga-card-border, #e5e7eb); border-radius: 8px; padding: 0.5rem 0.75rem; margin-top: 0.25rem; }
  .hsrb-split-note { font-size: 0.75rem; color: var(--yoga-text-muted, #737373); margin: 0 0 0.35rem; }
  .hsrb-actions { display: flex; gap: 0.75rem; }
  .hsrb-btn-primary, .hsrb-btn-secondary {
    padding: 0.75rem 1.25rem; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
    font-family: inherit; cursor: pointer; transition: all .15s;
  }
  .hsrb-btn-primary {
    flex: 1; background: var(--yoga-primary, #FF6B35); color: #fff; border: 1px solid var(--yoga-primary, #FF6B35);
  }
  .hsrb-btn-primary:hover:not(:disabled) { background: var(--yoga-primary-900, #1A1A2E); border-color: var(--yoga-primary-900, #1A1A2E); }
  .hsrb-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .hsrb-btn-secondary { background: transparent; color: var(--yoga-text, #0a0a0a); border: 1px solid var(--yoga-card-border, #d4d4d4); }
  .hsrb-btn-secondary:hover:not(:disabled) { background: rgba(0,0,0,0.04); }
  .hsrb-btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
  .hsrb-progress { font-size: 0.75rem; color: var(--yoga-text-muted, #737373); text-align: center; margin: 0; }
  @media (min-width: 640px) { .hsrb-progress { display: none; } }
`
